import {
  ChevronsUpDown,
  LogOut,
  Moon,
  Sun,
  Monitor,
  PanelLeft,
  Check,
  ShieldCheck,
  KeyRound,
  FileText,
  MessageCircleWarning,
} from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { useNavigate } from 'react-router';
import { useContext, useState } from 'react';
import { AuthContext } from '@/context/auth-context';
import { useLogout } from '@/hooks/use-logout';
import { ROUTES } from '@/constants/routes';
import { useTheme } from '@/providers/theme-provider';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { orgPath } from '@/utils/org-path';
import { useCannyIdentify } from '@/hooks/use-canny-identify';
import { useNavModeStore } from '@/stores/nav-mode-store';

const THEME_ICONS = { light: Sun, dark: Moon, system: Monitor };

export function NavUser() {
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const { user, isAdmin } = useContext(AuthContext);
  const logout = useLogout();
  const { setTheme, theme } = useTheme();
  const ThemeIcon = THEME_ICONS[theme as keyof typeof THEME_ICONS] ?? Monitor;
  const { isUserApiTokensEnabled, isFeedbackEnabled, isReleaseNotesEnabled } =
    useLaunchDarkly();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navMode = useNavModeStore((state) => state.mode);
  const toggleNavMode = useNavModeStore((state) => state.toggle);
  const cannyUrl = import.meta.env.VITE_CANNY_URL;
  // Submit Feedback is clickable the moment the menu opens, so the session has
  // to be attached before then rather than on open.
  useCannyIdentify(isFeedbackEnabled);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
            >
              <div className='relative'>
                <Avatar className='h-8 w-8 rounded-lg'>
                  <AvatarImage src={user?.image} alt={user?.email} />
                  <AvatarFallback className='rounded-lg'>
                    {user?.email[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                {isAdmin && (
                  <div className='absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary'>
                    <ShieldCheck className='h-2.5 w-2.5 text-primary-foreground' />
                  </div>
                )}
              </div>
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-medium'>{user?.email}</span>
              </div>
              <ChevronsUpDown className='ml-auto size-4' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg'
            side={isMobile ? 'bottom' : 'right'}
            align='end'
            sideOffset={4}
          >
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <ThemeIcon className='size-4' />
                Theme
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => setTheme('light')}>
                  <Sun className='size-4' />
                  Light
                  {theme === 'light' && <Check className='ml-auto size-4' />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('dark')}>
                  <Moon className='size-4' />
                  Dark
                  {theme === 'dark' && <Check className='ml-auto size-4' />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('system')}>
                  <Monitor className='size-4' />
                  System
                  {theme === 'system' && <Check className='ml-auto size-4' />}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuItem onClick={toggleNavMode}>
              <PanelLeft className='size-4' />
              Hide navigation labels
              {navMode === 'compact' && <Check className='ml-auto size-4' />}
            </DropdownMenuItem>
            {isUserApiTokensEnabled && (
              <DropdownMenuItem
                onClick={() => navigate(orgPath(ROUTES.ACCOUNT.API_TOKENS))}
              >
                <KeyRound className='size-4' />
                API Tokens
              </DropdownMenuItem>
            )}
            {/* These link out of the product, so they sit here rather than in
                the taskbar. */}
            <DropdownMenuItem asChild>
              <a
                href='https://www.capitol.ai/privacy'
                target='_blank'
                rel='noopener noreferrer'
              >
                <ShieldCheck className='size-4' />
                Privacy Policy
              </a>
            </DropdownMenuItem>
            {isFeedbackEnabled && cannyUrl && (
              <DropdownMenuItem asChild>
                {/* data-canny-link lets the identified SDK carry the session
                    into the portal; the plain href still works without it. */}
                <a
                  data-canny-link
                  href={cannyUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <MessageCircleWarning className='size-4' />
                  Submit Feedback
                </a>
              </DropdownMenuItem>
            )}
            {isReleaseNotesEnabled && (
              <DropdownMenuItem asChild>
                <a
                  id='nav-release-notes'
                  href='https://releases.capitol.ai'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <FileText className='size-4' />
                  Release Notes
                </a>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => logout()}>
              <LogOut className='size-4' />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
